/**
 * Shared types for the plain-language transaction compiler
 * (`compileTransaction` in `compile.ts`, workstream B).
 */
import type { Ore } from '../money';
import type { AssetDecision } from '../catalog/rules';
import type { AssetChoice, Category } from '../catalog/types';
import type {
  Account,
  AccountingMethod,
  EntityType,
  IsoDate,
  PaymentSource,
  TaxSubkind,
  TransactionKind,
  Uuid,
  VatChoice,
  VerifikationMeta,
  VerifikationRow,
} from '../types';
import type { VerifikationDraft } from '../verifikationer/rules';

export interface TransactionOptions {
  /** Representation: number of persons (300 kr VAT base per person). */
  persons?: number;
  /** Representation: full meal (6072) or enklare förtäring (6071). */
  mealType?: 'meal' | 'light';
  /**
   * Overrule the half-prisbasbelopp rule, in either direction.
   *
   * The amount alone does not settle it: K2 asks for a value of at least half a
   * prisbasbelopp *and* an expected useful life over three years. Equipment that
   * will be worn out within the year is a cost however much it cost, and a
   * cheaper thing that will last for years may be capitalised — the immediate
   * deduction below the threshold is something K2 allows, not something it
   * demands. Only the user knows which, so the card offers both ways.
   */
  assetChoice?: AssetChoice;
  /** "Faktura – betalas senare": book against 2440/1510 instead of the payment source. */
  deferPayment?: boolean;
  /** `settle`: the invoice verifikation being paid. */
  settlesVerifikationId?: Uuid;
  /** `refund`: the purchase being refunded (rows are mirrored). */
  refundOfVerifikationId?: Uuid;
}

export interface TransactionInput {
  kind: TransactionKind;
  taxSubkind?: TaxSubkind;
  categoryId?: string;
  paymentSourceId: Uuid;
  /** Transfers: the receiving payment source. */
  counterpartySourceId?: Uuid;
  /** Gross amount, always positive. */
  amountOre: Ore;
  date: IsoDate;
  counterparty: string;
  vat?: VatChoice;
  note?: string;
  documentIds: string[];
  options?: TransactionOptions;
}

export interface CompileContext {
  entityType: EntityType;
  vatRegistered: boolean;
  accountingMethod: AccountingMethod;
  /** The company's accounts by number (active and inactive). */
  accounts: ReadonlyMap<number, Account>;
  paymentSources: ReadonlyMap<Uuid, PaymentSource>;
  catalog: readonly Category[];
  /** Calendar year whose constants apply (prisbasbelopp, tax rate). */
  rulesYear: number;
  /** Rows of the verifikation a `refund`/`settle` refers to. */
  originalRows?: VerifikationRow[];
}

export interface CompileResult {
  draft: VerifikationDraft;
  meta: VerifikationMeta;
  /** BAS accounts used by the draft that are not yet active for the company. */
  accountsToActivate: number[];
  /** Swedish one-liner for DetailsDisclosure. */
  explanation: string;
  /** Present for a purchase whose category can be an inventarie: what the rule decided, and why. */
  asset?: AssetDecision;
}

export type CompileErrorCode =
  | 'unknown_category'
  | 'unknown_payment_source'
  | 'missing_category'
  | 'missing_counterparty_source'
  | 'missing_vat'
  | 'missing_tax_subkind'
  | 'missing_original'
  | 'invalid_amount'
  | 'unsupported_kind';

/** Thrown by `compileTransaction`. `message` is a Swedish sentence for the UI. */
export class CompileError extends Error {
  constructor(
    public readonly code: CompileErrorCode,
    message: string,
    /** The input field the user should fix, when there is one. */
    public readonly field?: keyof TransactionInput,
  ) {
    super(message);
    this.name = 'CompileError';
  }
}
